<template>
  <div>
    <div v-if="step === 1" class="cicc-grid">
      <div
        v-for="channel in channels"
        :key="channel.uuid"
        class="cicc-pick-card"
        @click="channelClick(channel)"
      >
        <div class="cicc-pick-icon" :class="`cicc-pick-icon--${channel.name}`">
          <i :class="channel.icon"></i>
        </div>
        <div class="cicc-pick-name">{{ channel.display_name }}</div>

        <button
          v-if="channel.name === 'instagram'"
          class="cicc-connect-btn cicc-connect-btn--ig"
          :disabled="loading"
        >
          <i class="fab fa-instagram" style="margin-right:6px"></i> Connect with Instagram
        </button>
        <button v-else class="cicc-connect-btn cicc-connect-btn--dark" :disabled="loading">
          Connect
        </button>
      </div>
    </div>

    <div v-else-if="step === 2" class="cicc-error-card">
      <i class="fal fa-exclamation-triangle cicc-error-icon"></i>
      <div class="cicc-error-title">We couldn't connect your Instagram profile</div>
      <p class="cicc-error-text">
        We couldn't connect your Instagram profile to the platform.
      </p>
      <div class="cicc-error-subhead">How do I solve this?</div>
      <ul class="cicc-error-list">
        <li>It has to be a public profile.</li>
        <li>It has to be a Business or Creator account.</li>
      </ul>
      <p class="cicc-error-text">
        You can read more here:
        <a
          href="https://makeinfluence.com/dk/influencer/blog/guides/saadan-opretter-du-en-virksomhedsprofil-paa-instagram"
          target="_blank"
        >How to create a business profile on Instagram</a>
      </p>
      <p class="cicc-error-text">
        If you're still experiencing issues, reach out to support@makeinfluence.com
      </p>
      <button class="cicc-retry-btn" @click="step = 1">Try again</button>
    </div>

    <div v-else>
      <div class="cicc-grid">
        <connect-influencer-channel-card
          v-for="influencerChannel in filteredInfluencerChannels"
          :key="influencerChannel.id"
          :influencer-channel="influencerChannel"
        ></connect-influencer-channel-card>
      </div>

      <div class="cicc-continue-row">
        <button class="cicc-continue-btn" @click="$emit('continue', true)">
          Continue
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import channel_color from "@/helpers/filters/channel_color";
import ConnectInfluencerChannelCard from "../cards/ConnectInfluencerChannelCard";

export default {
  components: {
    ConnectInfluencerChannelCard
  },
  filters: { channel_color },
  props: {
    influencerChannels: {
      default: () => [],
      required: false
    }
  },
  data: () => ({
    step: 1,
    channels: [],
    loading: false,
    iInfluencerChannels: []
  }),
  computed: {
    ...mapState("core/auth", ["user"]),
    filteredInfluencerChannels() {
      // If prop is set use that instead
      if (this.influencerChannels.length > 0) {
        return this.influencerChannels;
      }

      // Display non active profiles OR active that hasn't been approved or where api failed
      return this.iInfluencerChannels.filter(
        item =>
          !item.active ||
          (item.active && (!item.approved_at || item.api_failed_at))
      );
    },
    windowLink() {
      return window.location.href;
    }
  },
  methods: {
    ...mapActions("influencer", ["getInfluencerChannels", "getChannels"]),

    channelClick(channel) {
      this.trackVirtualPageView("channel-connect/login");
      this.trackVirtualPageView("channel-connect/login/" + channel.name);
      this.loading = true;
      const loginLink = channel.name === 'instagram'
        ? channel.login_link.replace('/c/instagram/login', '/c/instagram-direct/login')
        : channel.login_link;
      window.location.href = loginLink + "?user_uuid=" + this.user.uuid + "&redirectUrl=" + this.windowLink;
    }
  },
  watch: {
    step: {
      immediate: true,
      handler() {
        if (this.step === 2) {
          this.trackVirtualPageView("channel-connect/error");
        } else if (this.step === 3) {
          this.trackVirtualPageView("channel-connect/select-channels");
        }

        this.$emit("step", this.step);
      }
    }
  },
  created() {
    let params = this.$route.query;

    // Callback from server
    if (params.success !== undefined) {
      // If success show step 3
      if (params.success === "true") {
        this.getInfluencerChannels({
          influencer_uuid: this.user.influencer.uuid
        })
          .then(data => {
            this.iInfluencerChannels = data;
            if (data.length === 0) {
              this.step = 2;
            } else {
              this.step = 3;
            }
          })
          .catch(() => {
            this.loaded = true;
          });
      } else if (params.success === "false") {
        // If error show step 2 for error page..
        this.step = 2;
      }
    } else {
      this.trackVirtualPageView("channel-connect");
      this.getChannels().then(data => {
        this.channels = data;
      });
    }
  }
};
</script>

<style scoped>
.cicc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.cicc-pick-card {
  background: var(--surface, #fff);
  border: 1px solid var(--line, #eaecf0);
  border-radius: var(--r-lg, 14px);
  padding: 24px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s;
}
.cicc-pick-card:hover { border-color: var(--accent, #e05540); box-shadow: var(--shadow-sm, 0 1px 3px rgba(0,0,0,.08)); }

.cicc-pick-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #fff;
  margin-bottom: 12px;
}
.cicc-pick-icon--instagram { background: linear-gradient(45deg, #833AB4, #E1306C, #FCB045); }
.cicc-pick-icon--tiktok { background: #212121; }
.cicc-pick-icon--facebook { background: #3b5998; }

.cicc-pick-name { font-size: 15px; font-weight: 700; color: var(--ink-1, #111); margin-bottom: 16px; }

.cicc-pick-card button.cicc-connect-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  border: none;
  border-radius: var(--r-ctrl, 8px);
  color: #fff !important;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  pointer-events: none; /* whole card is the click target, per original behaviour */
}
.cicc-connect-btn--ig { background: linear-gradient(to right, #833AB4, #E1306C, #FCB045); }
.cicc-connect-btn--dark { background: #212121; }

.cicc-error-card {
  background: var(--surface, #fff);
  border: 1px solid var(--line, #eaecf0);
  border-radius: var(--r-lg, 14px);
  padding: 32px;
  max-width: 560px;
  text-align: center;
}
.cicc-error-icon { font-size: 32px; color: #e03e38; margin-bottom: 12px; }
.cicc-error-title { font-size: 16px; font-weight: 700; color: var(--ink-1, #111); margin-bottom: 12px; }
.cicc-error-text { font-size: 13.5px; color: var(--ink-2, #555); line-height: 1.6; text-align: left; }
.cicc-error-text a { color: var(--accent, #e05540); }
.cicc-error-subhead { font-size: 13.5px; font-weight: 700; color: var(--ink-1, #111); text-align: left; margin-top: 12px; }
.cicc-error-list { text-align: left; font-size: 13.5px; color: var(--ink-2, #555); line-height: 1.7; margin: 6px 0 6px 18px; }
.cicc-retry-btn {
  margin-top: 16px;
  height: 42px;
  padding: 0 24px;
  border: none;
  border-radius: var(--r-ctrl, 8px);
  background: var(--accent, #e05540);
  color: #fff;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
}
.cicc-retry-btn:hover { opacity: .9; }

.cicc-continue-row { text-align: center; margin-top: 24px; }
.cicc-continue-btn {
  height: 44px;
  padding: 0 32px;
  border: none;
  border-radius: var(--r-ctrl, 8px);
  background: var(--accent, #e05540);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.cicc-continue-btn:hover { opacity: .9; }
</style>